import { useQuery } from "@apollo/client";
import { useUserDataContext } from "contexts/UserDataContext/UserDataContext";
import {
    GetBusUnlockStatusQuery,
    GetBusUnlockStatusQueryVariables
} from "generated/graphql";
import { loader } from "graphql.macro";
import { Bus } from "types/busType";
import { canUnlockBuses } from "types/roleType";
import {
    hasuraSubscriptionsToNiraSubscriptions,
    maybeGetSubscriptionForRegion
} from "types/subscriptionType";
import {
    BusUnlockState,
    deriveBusUnlockState,
    LOADING_STATE,
    UNAVAILABLE_STATE,
    UNLOCK_ON_SELECT_STATE,
    UNLOCK_STATE_NEEDS_CONFIRMATION
} from "./busUnlockState";

const GET_BUS_UNLOCK_STATUS = loader("src/graphql/getBusUnlockStatus.graphql");

/**
 * Resolves the {@link BusUnlockState} for a selected bus: gathers whether the
 * team has already unlocked it and how many buses their subscription has left,
 * then defers to {@link deriveBusUnlockState}.
 */
const useBusUnlockState = (bus: Bus): BusUnlockState => {
    const user = useUserDataContext();
    const canUnlock = canUnlockBuses(user.role);

    // The unlock is charged to the subscription for the *bus'* region, which is
    // not necessarily the region being browsed -- buses from a neighboring
    // region show up on the map too.
    const region = bus.region;

    const maybeSubscription = maybeGetSubscriptionForRegion(
        user.subscriptions,
        region
    );

    // Cheap gate off the user data we already have: skip the round trip only
    // where a fresh answer could not change the outcome -- a reader can never
    // spend a bus, a region the team holds no subscription in has none to
    // spend, and an unlimited subscription is never asked about. Everything
    // else is bus-limited, where the login-time numbers move under us in both
    // directions: the team unlocks buses as they browse, and an Account Manager
    // can raise the limit mid-session. A limited plan that looks exhausted at
    // login is exactly the case that must not be trusted -- the server would
    // spend one of the newly added buses without the confirmation we owe.
    const skip =
        !canUnlock ||
        maybeSubscription === undefined ||
        maybeSubscription.unlimited;

    // What login-time data alone implies. Used only as the fallback when the
    // query below fails, and flagged unverified because login data carries no
    // per-bus unlock information at all: `isBusUnlocked` here is an assumption,
    // not an answer.
    const userDataUnlockState = deriveBusUnlockState({
        canUnlock,
        isBusUnlocked: false,
        maybeSubscription,
        isStatusVerified: false
    });

    const { data, loading, error } = useQuery<
        GetBusUnlockStatusQuery,
        GetBusUnlockStatusQueryVariables
    >(GET_BUS_UNLOCK_STATUS, {
        variables: { niraBusId: bus.niraBusId },
        skip,
        // The answer depends on how many buses the team has unlocked so far,
        // which moves as they unlock them, so read it fresh on every selection
        // rather than quoting a stale count back at the user -- and don't write
        // the bus' `unlocked` list into the cache, where it would go stale in
        // turn.
        fetchPolicy: "no-cache"
    });

    if (skip) {
        return UNLOCK_ON_SELECT_STATE;
    }

    if (error !== undefined) {
        // The status query failed, so there is no fresh count to quote, and
        // waiting on `LOADING_STATE` would strand the pane on a spinner with
        // nothing left to resolve it. Fall back to what login-time data can
        // still answer for: it under-counts what the team has spent, so a count
        // with buses left is enough to know we owe the confirmation.
        //
        // It cannot answer the other way. A count of zero may simply be stale
        // -- the plan may have been topped up since login -- and unlocking on
        // select there would spend one of the new buses without asking, which
        // is the whole thing this flow exists to prevent. Spend nothing and say
        // we couldn't tell instead.
        //
        // What login-time data cannot answer either way is whether the team has
        // already unlocked *this* bus, so the state below is marked unverified
        // and the prompt asks without quoting a count. Quoting one would tell a
        // team they are about to spend a bus on a bus they may already own.
        return userDataUnlockState.kind === UNLOCK_STATE_NEEDS_CONFIRMATION
            ? userDataUnlockState
            : UNAVAILABLE_STATE;
    }

    if (loading || data === undefined) {
        return LOADING_STATE;
    }

    const maybeQueriedBus = data.buses.find(
        (queriedBus) => queriedBus.nira_bus_id === bus.niraBusId
    );
    if (maybeQueriedBus === undefined) {
        // The response doesn't describe the selected bus (yet). Keep waiting
        // rather than falling through to an unlock the user hasn't agreed to.
        return LOADING_STATE;
    }

    return deriveBusUnlockState({
        canUnlock,
        isBusUnlocked: maybeQueriedBus.unlocked.length > 0,
        // Read the subscription back off this response rather than off user
        // data, whose count is stale as soon as the team unlocks anything.
        maybeSubscription: maybeGetSubscriptionForRegion(
            hasuraSubscriptionsToNiraSubscriptions(data.subscriptions),
            region
        )
    });
};

export default useBusUnlockState;
